import { getReferralProgram } from './api';
import { type WidgetConfig } from './config';
import { t } from './i18n';
import { openContextualLogin, markReferralShareReturn, clearReferralShareReturn } from './returnTo';
import { shareReferralLink } from './referralShare';
import { store } from './store';

/** Enhance an existing home referral CTA; never put a personal link in cached page HTML. */
export function initReferralShareBanner(config: WidgetConfig): void {
  if (config.referralProgramEnabled !== true || typeof window === 'undefined') return;
  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const homePath = new URL(config.viewUrls?.home || '/', window.location.origin).pathname.replace(/\/+$/, '') || '/';
  if (currentPath !== homePath) return;

  const accountPath = new URL(config.viewUrls?.['bonus-account'] || '/my-account/bonuses', window.location.origin).pathname.replace(/\/+$/, '');
  const cta = Array.from(document.querySelectorAll<HTMLAnchorElement>('main a[href]')).find((anchor) => {
    try {
      const target = new URL(anchor.href, window.location.origin);
      const referralTarget = anchor.dataset.swView === 'bonus-account'
        || (target.origin === window.location.origin && target.pathname.replace(/\/+$/, '') === accountPath);
      return referralTarget && !!anchor.closest('section, article');
    } catch { return false; }
  });
  if (!cta) return;
  const banner = cta.closest('section, article');
  if (banner && !document.getElementById('referral')) {
    const target = document.createElement('span');
    target.id = 'referral';
    banner.insertBefore(target, banner.firstChild);
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn btn-outline-primary ms-2';
  button.textContent = t('referralShare.button');
  button.hidden = true;
  button.style.display = 'none';
  cta.insertAdjacentElement('afterend', button);

  const setVisible = (visible: boolean) => {
    button.hidden = !visible;
    button.style.display = visible ? '' : 'none';
  };

  let link = '';
  let loadedFor = '';
  let loadVersion = 0;
  let authViewOpen = false;
  const sync = () => {
    if (store.getLoading()) { setVisible(false); return; }
    const auth = store.getAuth();
    if (!auth) {
      link = '';
      loadedFor = '';
      loadVersion++;
      setVisible(true);
      return;
    }
    const login = String(auth.login || '');
    if (!login || loadedFor === login) return;
    loadedFor = login;
    setVisible(false);
    const version = ++loadVersion;
    getReferralProgram(config).then((res) => {
      if (version !== loadVersion || !store.getAuth()) return;
      const program = res.status === 200 ? res.result : null;
      if (!program?.enabled || !/^https?:\/\//i.test(program.referral_link || '')) return;
      link = program.referral_link;
      setVisible(true);
      if (window.location.hash === '#referral') store.addToast(t('referralShare.ready'), 'success');
    }).catch(() => {});
  };

  button.addEventListener('click', () => {
    if (link && store.getAuth()) {
      void shareReferralLink(link).then((result) => {
        if (result === 'copied') store.addToast(t('referralShare.copied'), 'success');
        if (result === 'failed') store.addToast(t('referralShare.failed'), 'error');
      });
      return;
    }
    if (store.getAuth()) return;
    store.addToast(t('referralShare.signIn'), 'info');
    markReferralShareReturn();
    openContextualLogin();
  });

  store.subscribe(() => {
    const view = store.getAccountView();
    if (view === 'checkout-login' || view === 'signup') authViewOpen = true;
    else if (authViewOpen && !store.getAuth()) {
      clearReferralShareReturn();
      authViewOpen = false;
    }
    sync();
  });
  sync();
}
